import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Copy } from "lucide-react";
import { toast } from "sonner";

const DAYS = [
  { id: "monday", label: "Lundi" },
  { id: "tuesday", label: "Mardi" },
  { id: "wednesday", label: "Mercredi" },
  { id: "thursday", label: "Jeudi" },
  { id: "friday", label: "Vendredi" },
  { id: "saturday", label: "Samedi" },
  { id: "sunday", label: "Dimanche" },
];

const DEFAULT_DAY = { closed: false, lunch_open: "11:00", lunch_close: "14:30", dinner_open: "18:00", dinner_close: "22:30" };

export default function HoursPanel() {
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => {
      // ensure both hours objects exist with all days
      const ensure = (obj) => {
        const out = { ...(obj || {}) };
        DAYS.forEach(d => { if (!out[d.id]) out[d.id] = { ...DEFAULT_DAY }; });
        return out;
      };
      setS({
        ...data,
        hours: ensure(data.hours),
        hours_reservation: ensure(data.hours_reservation || data.hours),
      });
    });
  }, []);

  const save = async () => {
    try {
      const payload = {
        hours: s.hours,
        hours_reservation: s.hours_reservation,
        min_order: Number(s.min_order || 0),
        prep_time_minutes: Math.max(0, Math.round(Number(s.prep_time_minutes ?? 20))),
      };
      await api.put("/admin/settings", payload);
      toast.success("Horaires, minimum & temps de préparation enregistrés");
    } catch { toast.error("Erreur d'enregistrement"); }
  };

  const setDay = (key, day, field, val) => {
    setS({ ...s, [key]: { ...s[key], [day]: { ...s[key][day], [field]: val } } });
  };

  const copyTakeawayToReservation = () => {
    setS({ ...s, hours_reservation: JSON.parse(JSON.stringify(s.hours)) });
    toast.success("Horaires copiés depuis Emporter → Réservation. N'oubliez pas d'enregistrer.");
  };

  if (!s) return <div className="text-muted-foreground">Chargement…</div>;

  return (
    <div data-testid="hours-panel" className="max-w-5xl">
      <h1 className="font-display text-4xl mb-2">Horaires & minimum</h1>
      <p className="text-muted-foreground mb-8">Définissez des horaires <strong>indépendants</strong> pour les commandes à emporter et les réservations de table.</p>

      <div className="bg-white border border-border rounded-sm p-6 mb-6">
        <h2 className="font-display text-2xl mb-4">Temps de préparation moyen (emporter)</h2>
        <div className="max-w-xs">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Minutes</Label>
          <Input
            type="number"
            step="1"
            min="0"
            max="120"
            value={s.prep_time_minutes ?? 20}
            onChange={(e) => setS({ ...s, prep_time_minutes: e.target.value })}
            data-testid="prep-time-input"
            className="rounded-sm h-12"
          />
          <p className="text-xs text-muted-foreground mt-2">
            Affiché au client dès l&apos;ouverture du menu, sur le checkout et la page de confirmation
            (ex : &quot;Prêt dans ~20 min&quot;). Modifiable à tout moment selon l&apos;affluence.
          </p>
        </div>
      </div>

      <div className="bg-white border border-border rounded-sm p-6 mb-6">
        <h2 className="font-display text-2xl mb-4">Montant minimum de commande (emporter)</h2>
        <div className="max-w-xs">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Minimum (CHF)</Label>
          <Input type="number" step="0.5" min="0" value={s.min_order || 0} onChange={(e) => setS({ ...s, min_order: e.target.value })} data-testid="min-order-input" className="rounded-sm h-12" />
          <p className="text-xs text-muted-foreground mt-2">Mettez 0 pour désactiver.</p>
        </div>
      </div>

      <div className="bg-white border border-border rounded-sm p-6 mb-6">
        <Tabs defaultValue="takeaway">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <TabsList data-testid="hours-tabs">
              <TabsTrigger value="takeaway" data-testid="hours-tab-takeaway">Horaires emporter</TabsTrigger>
              <TabsTrigger value="reservation" data-testid="hours-tab-reservation">Horaires réservations</TabsTrigger>
            </TabsList>
            <button
              type="button"
              onClick={copyTakeawayToReservation}
              data-testid="copy-takeaway-btn"
              className="text-sm text-primary hover:underline flex items-center gap-1"
            ><Copy size={13} /> Copier Emporter → Réservation</button>
          </div>

          <TabsContent value="takeaway">
            <HoursGrid section="hours" hours={s.hours} setDay={setDay} />
            <p className="text-xs text-muted-foreground mt-3">Heures pendant lesquelles les clients peuvent commander à emporter en ligne.</p>
          </TabsContent>
          <TabsContent value="reservation">
            <HoursGrid section="hours_reservation" hours={s.hours_reservation} setDay={setDay} />
            <p className="text-xs text-muted-foreground mt-3">Heures pendant lesquelles les clients peuvent réserver une table (créneaux 15 min, fin -30 min).</p>
          </TabsContent>
        </Tabs>
      </div>

      <button onClick={save} data-testid="save-hours-btn" className="bg-primary text-white px-6 py-3 rounded-sm hover:bg-primary/90">Enregistrer</button>
    </div>
  );
}

function HoursGrid({ section, hours, setDay }) {
  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-6 gap-3 items-center text-xs uppercase tracking-wider text-muted-foreground pb-2 border-b border-border">
        <div>Jour</div><div>Statut</div><div>Midi début</div><div>Midi fin</div><div>Soir début</div><div>Soir fin</div>
      </div>
      {DAYS.map(d => {
        const h = hours?.[d.id] || {};
        return (
          <div key={d.id} className="grid sm:grid-cols-6 gap-3 items-center">
            <div className="font-medium">{d.label}</div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={!h.closed} onCheckedChange={(v) => setDay(section, d.id, "closed", !v)} data-testid={`${section}-${d.id}-toggle`} /> Ouvert</label>
            <Input disabled={h.closed} type="time" value={h.lunch_open || ""} onChange={(e) => setDay(section, d.id, "lunch_open", e.target.value)} data-testid={`${section}-${d.id}-lunch-open`} />
            <Input disabled={h.closed} type="time" value={h.lunch_close || ""} onChange={(e) => setDay(section, d.id, "lunch_close", e.target.value)} data-testid={`${section}-${d.id}-lunch-close`} />
            <Input disabled={h.closed} type="time" value={h.dinner_open || ""} onChange={(e) => setDay(section, d.id, "dinner_open", e.target.value)} data-testid={`${section}-${d.id}-dinner-open`} />
            <Input disabled={h.closed} type="time" value={h.dinner_close || ""} onChange={(e) => setDay(section, d.id, "dinner_close", e.target.value)} data-testid={`${section}-${d.id}-dinner-close`} />
          </div>
        );
      })}
    </div>
  );
}
